import React from 'react';
import {
  Box,
  Flex,
  Popover,
  PopoverArrow,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  Portal,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  PopoverBody,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  IconButton,
  Textarea,
  PopoverFooter,
  Tooltip,
  Text,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faCommentMedical, faEnvelopeDot, faNoteMedical } from '@fortawesome/pro-regular-svg-icons';

import { MessagesButton, Message, UnreadBadge, getUserAlias } from 'shared-components';
import { IMessage } from 'types';

import {
  AGENT_ROLES,
  ENROLY_AGENT_ROLES,
  IMMIGRATION_PROVIDER_ROLES,
  PARTNER_ROLES,
  UNI_ROLES,
  UserRole,
} from '../../../store/auth/types';
import { getTotalUnreadCount } from '../../../utilities/getTotalUnreadMsgCount';
import { t } from "i18next";

interface IProps {
  messages: Array<IMessage>;
  onSubmitMessage: (message: IMessage) => void;
  markMessagesRead: (messageIds: Array<string>) => void;
  markMessageUnread(messageId: string): void;
  userName: string;
  displayName: string;
  role: UserRole;
  visaAssistance: boolean;
  isAgent: boolean;
  hasAgentMessages: boolean;
  onLoadMessages: () => void;
}

interface IState {
  isMinimised: boolean;
  messages: Array<IMessage>;
  message: string;
  isSendToStudent: boolean;
  // UNI
  unreadMessagesUni: number;
  unreadMessagesStudentByUni: number;
  unreadMessagesAgentToUni: number;
  unreadMessagesUniSecure: number;
  // AGENT, AGENCY ADMIN
  unreadMessagesUniByAgent: number;
  unreadMessagesStudent: number;
  unreadMessagesUniToAgent: number;
  unreadMessagesStudentToAgent: number;
  // ADMIN, ENROLY
  unreadMessagesUniByAdmin: number;
  unreadMessagesUniToStudentByAdmin: number;
  unreadMessagesUniToAgentByAdmin: number;
  unreadMessagesStudentToAgentByAdmin: number;
  // PARTNER
  unreadMessagesUniByPartner: number;
  unreadMessagesUniToStudentByPartner: number;
  // IMMIGRATION PROVIDER
  unreadMessagesStudentByImmigrationProvider: number;
  unreadMessagesUniByImmigrationProvider: number;
  unreadMessagesUniToStudentByImmigrationProvider: number;
  hideMessages: boolean;
  hideNotes: boolean;
  isVisibleTooltip: boolean;
  tooltipIndex: number | null;
  tabIndex: number;
  tabIndexes: Map<number, string>;
  role: UserRole | string;
  visaAssistance: boolean;
  isAgent: boolean;
}

function excludeLogsWithoutNotes(message: IMessage): boolean {
  return !message.isSendToStudent &&
    ['log', 'note'].includes(message.messageType) &&
    message.messageFrom === message.messageTo
    ? message.message.split(':').length > 1
    : true;
}

function isNote(message: IMessage): boolean {
  return (
    message.messageType === 'personalNote' ||
    (['note', 'secure', 'agentStudent', 'partner', 'immigrationProvider'].includes(message.messageType) &&
      message.isSendToStudent !== true)
  );
}

function isMessage(message: IMessage): boolean {
  return (
    ['student', 'agentUni'].includes(message.messageType) ||
    message.isSendToStudent ||
    (message.messageType === 'log' && message.messageFrom === 'system')
  );
}

export class MessageInbox extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      isMinimised: true,
      messages: props.messages ? props.messages : [],
      message: '',
      isSendToStudent: false,
      // UNI
      unreadMessagesUni: this.getUnreadMessagesFromStudentToUni(props.messages, 'uni').length ?? 0,
      unreadMessagesStudentByUni: this.getUnreadMessagesFromUniToStudent(props.messages, 'uni').length ?? 0,
      unreadMessagesAgentToUni: this.getUnreadMessagesFromAgentToUni(props.messages).length ?? 0,
      unreadMessagesUniSecure: this.getUnreadMessagesFromStudentToUniSecure(props.messages).length ?? 0,
      // AGENT, AGENCY ADMIN
      unreadMessagesUniByAgent: this.getUnreadMessagesFromStudentToUni(props.messages, 'agent').length ?? 0,
      unreadMessagesStudent: this.getUnreadMessagesFromUniToStudent(props.messages, 'agent').length ?? 0,
      unreadMessagesUniToAgent: this.getUnreadMessagesFromUniToAgent(props.messages, 'agent').length ?? 0,
      unreadMessagesStudentToAgent: this.getUnreadMessagesFromStudentToAgent(props.messages, 'agent').length ?? 0,
      // ADMIN, ENROLY
      unreadMessagesUniByAdmin: this.getUnreadMessagesFromStudentToUni(props.messages, 'admin').length ?? 0,
      unreadMessagesUniToStudentByAdmin: this.getUnreadMessagesFromUniToStudent(props.messages, 'admin').length ?? 0,
      unreadMessagesUniToAgentByAdmin: this.getUnreadMessagesFromUniToAgent(props.messages, 'admin').length ?? 0,
      unreadMessagesStudentToAgentByAdmin:
        this.getUnreadMessagesFromStudentToAgent(props.messages, 'admin').length ?? 0,
      // PARTNER
      unreadMessagesUniByPartner: this.getUnreadMessagesFromStudentToUni(props.messages, 'partner').length ?? 0,
      unreadMessagesUniToStudentByPartner:
        this.getUnreadMessagesFromUniToStudent(props.messages, 'partner').length ?? 0,

      // IMMIGRATION PROVIDER
      unreadMessagesStudentByImmigrationProvider:
        this.getUnreadMessagesFromStudentToImmigrationProvider(props.messages).length ?? 0,
      unreadMessagesUniByImmigrationProvider:
        this.getUnreadMessagesFromStudentToUni(props.messages, 'immigrationProvider').length ?? 0,
      unreadMessagesUniToStudentByImmigrationProvider:
        this.getUnreadMessagesFromUniToStudent(props.messages, 'immigrationProvider').length ?? 0,
      hideMessages: false,
      hideNotes: false,
      isVisibleTooltip: false,
      tooltipIndex: null,
      tabIndex: 0,
      role: props.role || '',
      visaAssistance: props.visaAssistance ? props.visaAssistance : false,
      isAgent: props.isAgent ? props.isAgent : false,
      tabIndexes: this.getTabIndexes(props.role, props.visaAssistance, props.isAgent),
    };
  }

  componentDidUpdate(prevProps: IProps) {
    if (this.props.messages !== prevProps.messages && this.props.messages) {
      const { messages } = this.props;
      this.setState({
        messages,
        // UNI
        unreadMessagesUni: this.getUnreadMessagesFromStudentToUni(this.props.messages, 'uni').length ?? 0,
        unreadMessagesStudentByUni: this.getUnreadMessagesFromUniToStudent(this.props.messages, 'uni').length ?? 0,
        unreadMessagesAgentToUni: this.getUnreadMessagesFromAgentToUni(this.props.messages).length ?? 0,
        unreadMessagesUniSecure: this.getUnreadMessagesFromStudentToUniSecure(this.props.messages).length ?? 0,
        // AGENT, AGENCY ADMIN
        unreadMessagesUniByAgent: this.getUnreadMessagesFromStudentToUni(this.props.messages, 'agent').length ?? 0,
        unreadMessagesStudent: this.getUnreadMessagesFromUniToStudent(this.props.messages, 'agent').length ?? 0,
        unreadMessagesUniToAgent: this.getUnreadMessagesFromUniToAgent(this.props.messages, 'agent').length ?? 0,
        unreadMessagesStudentToAgent:
          this.getUnreadMessagesFromStudentToAgent(this.props.messages, 'agent').length ?? 0,
        // ADMIN, ENROLY
        unreadMessagesUniByAdmin: this.getUnreadMessagesFromStudentToUni(this.props.messages, 'admin').length ?? 0,
        unreadMessagesUniToStudentByAdmin:
          this.getUnreadMessagesFromUniToStudent(this.props.messages, 'admin').length ?? 0,
        unreadMessagesUniToAgentByAdmin: this.getUnreadMessagesFromUniToAgent(this.props.messages, 'admin').length ?? 0,
        unreadMessagesStudentToAgentByAdmin:
          this.getUnreadMessagesFromStudentToAgent(this.props.messages, 'admin').length ?? 0,
        // PARTNER
        unreadMessagesUniByPartner: this.getUnreadMessagesFromStudentToUni(this.props.messages, 'partner').length ?? 0,
        unreadMessagesUniToStudentByPartner:
          this.getUnreadMessagesFromUniToStudent(this.props.messages, 'partner').length ?? 0,
        // IMMIGRATION PROVIDER
        unreadMessagesUniToStudentByImmigrationProvider:
          this.getUnreadMessagesFromUniToStudent(this.props.messages, 'immigrationProvider').length ?? 0,
        unreadMessagesStudentByImmigrationProvider:
          this.getUnreadMessagesFromStudentToImmigrationProvider(this.props.messages).length ?? 0,
        unreadMessagesUniByImmigrationProvider:
          this.getUnreadMessagesFromStudentToUni(this.props.messages, 'immigrationProvider').length ?? 0,
        tabIndexes: this.getTabIndexes(this.props.role, this.props.visaAssistance, this.props.isAgent),
      });
    }

    if (this.props.role !== prevProps.role && this.props.role) {
      this.setState({
        role: this.props.role,
        tabIndexes: this.getTabIndexes(this.props.role, this.props.visaAssistance, this.props.isAgent),
      });
    }

    if (this.props.visaAssistance !== prevProps.visaAssistance && this.props.visaAssistance) {
      this.setState({
        visaAssistance: this.props.visaAssistance,
        tabIndexes: this.getTabIndexes(this.props.role, this.props.visaAssistance, this.props.isAgent),
      });
    }

    if (this.props.isAgent !== prevProps.isAgent && this.props.isAgent) {
      this.setState({
        isAgent: this.props.isAgent,
        tabIndexes: this.getTabIndexes(this.props.role, this.props.visaAssistance, this.props.isAgent),
      });
    }
  }

  getTabIndexes = (role: string, visaAssistance = false, isAgent = false) => {
    switch (role) {
      case '[Uni]':
      case '[UniAdmin]':
      case '[UniLite]':
        if (visaAssistance || isAgent) {
          return new Map([
            [0, 'note'],
            [1, 'agentUni'],
          ]);
        }
        return new Map([[0, 'note']]);

      case '[UniSecure]':
        if (visaAssistance || isAgent) {
          return new Map([
            [0, 'note'],
            [1, 'agentUni'],
            [2, 'secure'],
          ]);
        }
        return new Map([
          [0, 'note'],
          [1, 'secure'],
        ]);

      case '[Enroly]':
      case '[Agent]':
      case '[Admin]':
      case '[AgencyAdmin]':
        return new Map([
          [0, 'note'],
          [1, 'agentUni'],
          [2, 'agentStudent'],
        ]);
      case '[ImmigrationProvider]':
        return new Map([
          [0, 'note'],
          [1, 'immigrationProvider'],
        ]);
      default:
        return new Map([[0, 'note']]);
    }
  };

  getUnreadMessagesFromUniToStudent = (messages: Array<IMessage>, role: string): Array<IMessage> => {
    // comment build frontend
    return this.filterMessagesByUniStudent(messages).filter(
      message =>
        (role === 'uni' &&
          message.isSendToStudent &&
          message.messageFrom !== 'student' &&
          message.uniRead !== true &&
          (message.messageFrom !== 'uni' || message.messageTo !== 'uni')) ||
        (message.messageFrom !== 'system' &&
          message.messageTo === 'student' &&
          message.messageTo !== message.messageFrom &&
          ((role === 'uni' && message?.uniRead !== true) ||
            (role === 'admin' && message?.adminRead !== true) ||
            (role === 'agent' && message?.agentRead !== true) ||
            (role === 'partner' && message?.partnerRead !== true) ||
            (role === 'immigrationProvider' && message?.immigrationProviderRead !== true))),
    );
  };

  getUnreadMessagesFromStudentToUni = (messages: Array<IMessage>, role: string): Array<IMessage> => {
    return this.filterMessagesByUniStudent(messages).filter(
      message =>
        message.messageTo !== 'student' &&
        (message.messageTo !== message.messageFrom ||
          (this.state?.role === UserRole.Partner &&
            message.messageFrom !== 'partner' &&
            message.messageType !== 'personalNote')) &&
        ((role === 'uni' && message?.uniRead !== true) ||
          (role === 'admin' && message?.adminRead !== true) ||
          (role === 'agent' && message?.agentRead !== true) ||
          (role === 'partner' && message?.partnerRead !== true) ||
          (role === 'immigrationProvider' && message?.immigrationProviderRead !== true)),
    );
  };

  getUnreadMessagesFromUniToAgent = (messages: Array<IMessage>, role: string): Array<IMessage> => {
    return this.filterMessagesByAgentUni(messages).filter(
      message =>
        message.messageTo === 'agent' &&
        message.messageTo !== message.messageFrom &&
        ((role === 'admin' && message?.adminRead !== true) || (role === 'agent' && message?.agentRead !== true)),
    );
  };

  getUnreadMessagesFromAgentToUni = (messages: Array<IMessage>): Array<IMessage> => {
    return this.filterMessagesByAgentUni(messages).filter(
      message =>
        message.messageTo !== 'agent' && message?.uniRead !== true && message.messageTo !== message.messageFrom,
    );
  };

  getUnreadMessagesFromStudentToAgent = (messages: Array<IMessage>, role: string): Array<IMessage> => {
    return this.filterMessagesByAgentStudent(messages).filter(
      message =>
        message.messageTo === 'agent' &&
        message.messageTo !== message.messageFrom &&
        ((role === 'admin' && message?.adminRead !== true) || (role === 'agent' && message?.agentRead !== true)),
    );
  };

  getUnreadMessagesFromStudentToUniSecure = (messages: Array<IMessage>): Array<IMessage> => {
    return this.filterMessagesBySecureStudent(messages).filter(
      message =>
        (message.messageTo !== 'student' ||
          (message.messageType === 'note' && message.messageFrom === 'agent' && message.isSendToStudent)) &&
        message?.uniRead !== true &&
        message.messageTo !== message.messageFrom,
    );
  };

  getUnreadMessagesFromStudentToImmigrationProvider = (messages: Array<IMessage>): Array<IMessage> => {
    return this.filterMessagesByImmigrationProviderStudent(messages).filter(
      message =>
        message.messageTo !== 'student' &&
        message?.immigrationProviderRead !== true &&
        message.messageTo !== message.messageFrom,
    );
  };

  filterMessagesByType = (messages: Array<IMessage>, tabType: string | undefined): Array<IMessage> => {
    switch (tabType) {
      case 'agentUni':
        return this.filterMessagesByAgentUni(messages);
      case 'agentStudent':
        return this.filterMessagesByAgentStudent(messages);
      case 'secure':
        return this.filterMessagesBySecureStudent(messages);
      case 'immigrationProvider':
        return this.filterMessagesByImmigrationProviderStudent(messages);
      default:
        return this.filterMessagesByUniStudent(messages);
    }
  };

  getTabDescriptionByType = (tabType: string) => {
    switch (tabType) {
      case 'agentUni':
        return 'This channel is private between the University and agent';
      case 'agentStudent':
        return 'This channel is private between the student and agent';
      case 'secure':
        return 'This channel is private between the University and student';
      case 'immigrationProvider':
        return 'This channel is private between the student and immigration provider';
      default:
        return 'This channel is open between the student and university, as well as agent, service, and immigration providers where applicable';
    }
  };

  filterMessagesByAgentUni = (messages: Array<IMessage>): Array<IMessage> => {
    if (!messages || !Array.isArray(messages)) {
      return [];
    }
    return messages
      .filter(
        message =>
          message.messageType === 'agentUni' || (message.messageType === 'agent' && message.messageFrom === 'uni'),
      )
      .filter(message => excludeLogsWithoutNotes(message));
  };

  filterMessagesByAgentStudent = (messages: Array<IMessage>): Array<IMessage> => {
    if (!messages || !Array.isArray(messages)) {
      return [];
    }
    return messages
      .filter(
        message =>
          message.messageType === 'agentStudent' ||
          (message.messageType === 'agent' && message.messageFrom === 'student'),
      )
      .filter(message => excludeLogsWithoutNotes(message));
  };

  filterMessagesByImmigrationProviderStudent = (messages: Array<IMessage>): Array<IMessage> => {
    if (!messages || !Array.isArray(messages)) {
      return [];
    }
    return messages
      .filter(
        message =>
          message.messageType === 'immigrationProvider' ||
          (message.messageType === 'immigrationProvider' && message.messageFrom === 'student'),
      )
      .filter(message => excludeLogsWithoutNotes(message));
  };

  filterMessagesBySecureStudent = (messages: Array<IMessage>): Array<IMessage> => {
    if (!messages || !Array.isArray(messages)) {
      return [];
    }
    return messages
      .filter(message => message.messageType === 'secure')
      .filter(message => excludeLogsWithoutNotes(message));
  };

  filterMessagesByUniStudent = (messages: Array<IMessage>): Array<IMessage> => {
    if (!messages || !Array.isArray(messages)) {
      return [];
    }
    return messages
      .filter(message => {
        const filterByMsgType =
          message.messageType !== '' &&
          message.messageType !== 'secure' &&
          message.messageType !== 'agent' &&
          message.messageType !== 'agentStudent' &&
          message.messageType !== 'agentUni' &&
          message.messageType !== 'immigrationProvider';
        const filterUniNote =
          !['uni', 'partner', 'immigrationProvider'].includes(message.messageFrom) &&
          message.messageType === 'personalNote';
        const filterPartnerNote =
          !['admin', 'partner', 'uni'].includes(message.messageFrom) && message.messageType === 'personalNote';
        const filterimmigrationProviderNote =
          !['admin', 'uni', 'immigrationProvider'].includes(message.messageFrom) &&
          message.messageType === 'personalNote';
        const filterAdminNote =
          !['admin', 'enroly', 'partner', 'immigrationProvider'].includes(message.messageFrom) &&
          message.messageType === 'personalNote';
        const filterAgentNote =
          !['agent', 'student'].includes(message.messageFrom) &&
          ((message.messageTo === 'uni' && !message.isSendToStudent) || message.messageType === 'personalNote');

        switch (this.props.role) {
          case '[Admin]':
          case '[Enroly]':
            return !filterAdminNote && filterByMsgType;
          case '[AgencyAdmin]':
          case '[Agent]':
            return !filterAgentNote && filterByMsgType;
          case '[UniSecure]':
          case '[Uni]':
          case '[UniAdmin]':
          case '[UniLite]':
            return !filterUniNote && filterByMsgType;
          case '[Partner]':
            return !filterPartnerNote && filterByMsgType;
          case '[ImmigrationProvider]':
            return !filterimmigrationProviderNote && filterByMsgType;
          default:
            return filterByMsgType;
        }
      })
      .filter(message => excludeLogsWithoutNotes(message));
  };

  onClickMinimisedInbox = (role: string, unreadCount: number) => {
    if (this.state.isMinimised && this.state.messages?.length === 0) {
      this.props.onLoadMessages();
    }
    switch (role) {
      case UserRole.Uni:
      case UserRole.UniAdmin:
      case UserRole.UniLite:
      case UserRole.UniSecure: {
        if (this.state.unreadMessagesUni > 0) {
          this.setState(
            {
              isMinimised: !this.state.isMinimised,
              unreadMessagesUni: 0,
            },
            () => {
              this.props.markMessagesRead(
                this.getUnreadMessagesFromStudentToUni(this.state.messages, 'uni').map(message => message.messageId!),
              );
            },
          );
        } else {
          this.setState({
            isMinimised: !this.state.isMinimised,
          });
        }
        if (unreadCount > 0 && this.state.unreadMessagesStudentByUni > 0) {
          this.setState(
            {
              isMinimised: !this.state.isMinimised,
              unreadMessagesStudentByUni: 0,
            },
            () => {
              this.props.markMessagesRead(
                this.getUnreadMessagesFromUniToStudent(this.state.messages, 'uni').map(message => message.messageId!),
              );
            },
          );
        } else {
          this.setState({
            isMinimised: !this.state.isMinimised,
          });
        }
        break;
      }
      case UserRole.Admin:
      case UserRole.Enroly: {
        if (this.state.unreadMessagesUniByAdmin > 0) {
          this.setState(
            {
              isMinimised: !this.state.isMinimised,
              unreadMessagesUniByAdmin: 0,
            },
            () => {
              this.props.markMessagesRead(
                this.getUnreadMessagesFromStudentToUni(this.state.messages, 'admin').map(message => message.messageId!),
              );
            },
          );
        } else {
          this.setState({
            isMinimised: !this.state.isMinimised,
          });
        }
        if (this.state.unreadMessagesUniToStudentByAdmin > 0) {
          this.setState(
            {
              isMinimised: !this.state.isMinimised,
              unreadMessagesUniToStudentByAdmin: 0,
            },
            () => {
              this.props.markMessagesRead(
                this.getUnreadMessagesFromUniToStudent(this.state.messages, 'admin').map(message => message.messageId!),
              );
            },
          );
        } else {
          this.setState({
            isMinimised: !this.state.isMinimised,
          });
        }
        break;
      }
      case UserRole.Agent:
      case UserRole.AgencyAdmin: {
        if (this.state.unreadMessagesUniByAgent > 0) {
          this.setState(
            {
              isMinimised: !this.state.isMinimised,
              unreadMessagesUniByAgent: 0,
            },
            () => {
              this.props.markMessagesRead(
                this.getUnreadMessagesFromStudentToUni(this.state.messages, 'agent').map(message => message.messageId!),
              );
            },
          );
        } else {
          this.setState({
            isMinimised: !this.state.isMinimised,
          });
        }
        if (this.state.unreadMessagesStudent > 0) {
          this.setState(
            {
              isMinimised: !this.state.isMinimised,
              unreadMessagesStudent: 0,
            },
            () => {
              this.props.markMessagesRead(
                this.getUnreadMessagesFromUniToStudent(this.state.messages, 'agent').map(message => message.messageId!),
              );
            },
          );
        } else {
          this.setState({
            isMinimised: !this.state.isMinimised,
          });
        }
        break;
      }
      case UserRole.Partner: {
        if (this.state.unreadMessagesUniByPartner > 0) {
          this.setState(
            {
              isMinimised: !this.state.isMinimised,
              unreadMessagesUniByPartner: 0,
            },
            () => {
              this.props.markMessagesRead([
                ...this.getUnreadMessagesFromStudentToUni(this.state.messages, 'partner').map(
                  message => message.messageId!,
                ),
                ...this.getUnreadMessagesFromUniToStudent(this.state.messages, 'partner').map(
                  message => message.messageId!,
                ),
              ]);
            },
          );
        } else {
          this.setState({
            isMinimised: !this.state.isMinimised,
          });
        }
        if (this.state.unreadMessagesUniToStudentByPartner > 0) {
          this.setState(
            {
              isMinimised: !this.state.isMinimised,
              unreadMessagesUniToStudentByPartner: 0,
            },
            () => {
              this.props.markMessagesRead(
                this.getUnreadMessagesFromUniToStudent(this.state.messages, 'partner').map(
                  message => message.messageId!,
                ),
              );
            },
          );
        } else {
          this.setState({
            isMinimised: !this.state.isMinimised,
          });
        }
        break;
      }
      case UserRole.ImmigrationProvider: {
        if (
          this.state.unreadMessagesUniByImmigrationProvider > 0 ||
          this.state.unreadMessagesUniToStudentByImmigrationProvider > 0
        ) {
          this.setState(
            {
              isMinimised: !this.state.isMinimised,
              unreadMessagesUniByImmigrationProvider: 0,
              unreadMessagesUniToStudentByImmigrationProvider: 0,
            },
            () => {
              this.props.markMessagesRead([
                ...this.getUnreadMessagesFromStudentToUni(this.props.messages, 'immigrationProvider').map(
                  message => message.messageId!,
                ),
                ...this.getUnreadMessagesFromUniToStudent(this.props.messages, 'immigrationProvider').map(
                  message => message.messageId!,
                ),
              ]);
            },
          );
        } else {
          this.setState({
            isMinimised: !this.state.isMinimised,
          });
        }
        break;
      }
    }
  };

  getAuthorRole = (message: IMessage) => {
    switch (message.messageFrom) {
      case 'uni':
        return 'uni';

      case 'enroly':
        return 'enroly';

      case 'student':
        return 'student';

      case 'agent':
        return 'agent';

      case 'partner':
      case 'immigrationProvider':
        return 'partner';

      case 'system':
        return 'system';

      default:
        return undefined;
    }
  };

  getMessageStyle = (message: IMessage) => {
    if (isNote(message)) {
      if (this.props.userName === message.author) {
        return 'note-outgoing';
      }
      return 'note';
    } else if (message.messageType === 'log' || message.messageType === 'welcome' || message.messageType === 'system') {
      return 'system';
    }
    if (this.props.userName === message.author) {
      return 'outgoing';
    }
    return 'incoming';
  };

  transformMessages = (messages: Array<IMessage>) => {
    if (!messages || !Array.isArray(messages)) {
      return [];
    }

    if (this.state.hideMessages) {
      messages = messages.filter(message => !isMessage(message));
    }

    if (this.state.hideNotes) {
      messages = messages.filter(message => !isNote(message));
    }

    return messages.map((message, index) => {
      const isMessageSaving = message.isMessageSaving || false;
      const isMessageError = message.isMessageError || false;
      const authorName = message.authorName || message.author;

      return (
        <Message
          key={`message-field-item-${index}`}
          {...({
            messageId: message.messageId,
            channel: message.channel,
            messageFrom: message.messageFrom,
            message: message.message,
            isSendToStudent: message.isSendToStudent,
            messageType: message.messageType,
            createdAt: message.createdAt,
            author: authorName,
            authorName: message.authorName,
            messageTo: message.messageTo,
            read: message.read,
            uniRead: message.uniRead,
            agentRead: message.agentRead,
            adminRead: message.adminRead,
            partnerRead: message.partnerRead,
            immigrationProviderRead: message.immigrationProviderRead,
            disableNotifications: message.disableNotifications,
            isMessageSaving,
            isMessageError,
            messageStyle: this.getMessageStyle(message),
            authorStyle: this.getAuthorRole(message),
            headerSize: 'sm' as const,
            hasAvatar: false,
          } as IMessage & {
            messageStyle?: 'outgoing' | 'incoming' | 'system' | 'note' | 'note-outgoing';
            authorStyle?: 'uni' | 'enroly' | 'student' | 'agent' | 'partner' | 'system';
            headerSize?: 'lg' | 'md' | 'sm' | 'xs';
            hasAvatar?: boolean;
            actions?: Array<{
              type: 'action' | 'divider';
              onClick?: () => void;
              label?: string;
            }>;
          })}
          actions={
            message.messageTo === 'uni' && ['student', 'agent'].includes(message.messageFrom)
              ? [
                  {
                    type: 'action',
                    label: 'Mark as unread',
                    onClick: () => this.props.markMessageUnread(message.messageId),
                    icon: faEnvelopeDot,
                  },
                ]
              : undefined
          }
          //   {
          //     type: 'action',
          //     label: 'Edit',
          //     onClick: undefined,
          //     icon: faEdit
          //   },
          //   {
          //     type: 'divider'
          //   },
          //   {
          //     type: 'action',
          //     label: 'Delete',
          //     onClick: undefined,
          //     icon: faTrashAlt
          //   }
          // ]}
        />
      );
    });
  };

  submitMessage = (isSendToStudent: boolean) => {
    if (!this.state.message) {
      return;
    }
    const messageTypeState = this.state.tabIndexes.get(this.state.tabIndex) || '';
    const messageType = messageTypeState === 'note' && !isSendToStudent ? 'personalNote' : messageTypeState;
    const userAlias = getUserAlias();
    const author = this.props.userName || 'Anonymous';
    const authorName = userAlias || this.props.displayName || author;
    const messageFrom = this.getFrom(this.state.role);
    const messageTo = this.getTo(messageType, messageFrom);
    const newMessage: IMessage = {
      messageId: `${Date.now()}`,
      message: this.state.message,
      createdAt: Date.now(),
      isSendToStudent,
      messageType,
      author,
      authorName,
      isMessageSaving: true,
      isMessageError: false,
      messageFrom,
      messageTo,
      ...(UNI_ROLES.includes(this.state.role) ? { uniRead: true } : {}),
      ...(ENROLY_AGENT_ROLES.includes(this.state.role) ? { adminRead: true } : {}),
      ...(AGENT_ROLES.includes(this.state.role) ? { agentRead: true } : {}),
      ...(PARTNER_ROLES.includes(this.state.role) ? { partnerRead: true } : {}),
      ...(IMMIGRATION_PROVIDER_ROLES.includes(this.state.role) ? { immigrationProviderRead: true } : {}),
    };

    this.props.onSubmitMessage(newMessage);

    const { messages } = this.state;
    messages.push(newMessage);
    this.setState({
      messages,
      message: '',
      isVisibleTooltip: false,
      tooltipIndex: null,
    });
  };

  getFrom = (role: string) => {
    if (ENROLY_AGENT_ROLES.includes(role)) return 'enroly';
    if (AGENT_ROLES.includes(role)) return 'agent';
    if (PARTNER_ROLES.includes(role)) return 'partner';
    if (IMMIGRATION_PROVIDER_ROLES.includes(role)) return 'immigrationProvider';
    return 'uni';
  };

  getTo = (messageType: string, from: string) => {
    switch (messageType) {
      case 'agentUni':
        return from === 'uni' ? 'agent' : 'uni';
      case 'agentStudent':
      case 'immigrationProvider':
      case 'secure':
        return 'student';
      case 'agent':
        return 'agent';
      case 'note':
        if (this.state.isSendToStudent) return 'student';
        return 'uni';
      case 'personalNote':
        return from;
      default:
        return 'uni';
    }
  };

  updateUnreads = (index: number, role: string) => {
    const tab = this.state.tabIndexes.get(index);
    switch (tab) {
      case 'note':
      default:
        switch (role) {
          case UserRole.Uni:
          case UserRole.UniAdmin:
          case UserRole.UniLite:
          case UserRole.UniSecure: {
            if (this.state.unreadMessagesUni > 0) {
              this.setState({
                unreadMessagesUni: 0,
              });
              this.props.markMessagesRead(
                this.getUnreadMessagesFromStudentToUni(this.props.messages, 'uni').map(message => message.messageId!),
              );
            }
            if (this.state.unreadMessagesStudentByUni > 0) {
              this.setState({
                unreadMessagesStudentByUni: 0,
              });
              this.props.markMessagesRead(
                this.getUnreadMessagesFromUniToStudent(this.props.messages, 'uni').map(message => message.messageId!),
              );
            }
            break;
          }
          case UserRole.Admin:
          case UserRole.Enroly: {
            if (this.state.unreadMessagesUniByAdmin > 0) {
              this.setState({
                unreadMessagesUniByAdmin: 0,
              });
              this.props.markMessagesRead(
                this.getUnreadMessagesFromStudentToUni(this.props.messages, 'admin').map(message => message.messageId!),
              );
            }
            if (this.state.unreadMessagesUniToStudentByAdmin > 0) {
              this.setState({
                unreadMessagesUniToStudentByAdmin: 0,
              });
              this.props.markMessagesRead(
                this.getUnreadMessagesFromUniToStudent(this.props.messages, 'admin').map(message => message.messageId!),
              );
            }
            break;
          }
          case UserRole.Agent:
          case UserRole.AgencyAdmin: {
            if (this.state.unreadMessagesUniByAgent > 0) {
              this.setState({
                unreadMessagesUniByAgent: 0,
              });
              this.props.markMessagesRead(
                this.getUnreadMessagesFromStudentToUni(this.props.messages, 'agent').map(message => message.messageId!),
              );
            }
            if (this.state.unreadMessagesStudent > 0) {
              this.setState({
                unreadMessagesStudent: 0,
              });
              this.props.markMessagesRead(
                this.getUnreadMessagesFromUniToStudent(this.props.messages, 'agent').map(message => message.messageId!),
              );
            }
            break;
          }
          case UserRole.ImmigrationProvider: {
            if (this.state.unreadMessagesStudentByImmigrationProvider > 0 && tab === 'immigrationProvider') {
              this.setState({
                unreadMessagesStudentByImmigrationProvider: 0,
              });
              this.props.markMessagesRead(
                this.getUnreadMessagesFromStudentToImmigrationProvider(this.props.messages).map(
                  message => message.messageId!,
                ),
              );
            }
            if (this.state.unreadMessagesUniByImmigrationProvider > 0) {
              this.setState({
                unreadMessagesUniByImmigrationProvider: 0,
              });
              this.props.markMessagesRead(
                this.getUnreadMessagesFromStudentToUni(this.props.messages, 'immigrationProvider').map(
                  message => message.messageId!,
                ),
              );
            }
          }
        }
        break;
      case 'agent':
        if (this.state.unreadMessagesAgentToUni > 0) {
          this.setState({
            unreadMessagesAgentToUni: 0,
          });
          this.props.markMessagesRead(
            this.getUnreadMessagesFromAgentToUni(this.props.messages).map(message => message.messageId!),
          );
        }
        break;
      case 'secure':
        if (this.state.unreadMessagesUniSecure > 0) {
          this.setState({
            unreadMessagesUniSecure: 0,
          });
          this.props.markMessagesRead(
            this.getUnreadMessagesFromStudentToUniSecure(this.props.messages).map(message => message.messageId!),
          );
        }
        break;
      case 'agentUni':
        switch (role) {
          case UserRole.Uni:
          case UserRole.UniAdmin:
          case UserRole.UniLite:
          case UserRole.UniSecure: {
            if (this.state.unreadMessagesAgentToUni > 0) {
              this.setState({
                unreadMessagesAgentToUni: 0,
              });
              this.props.markMessagesRead(
                this.getUnreadMessagesFromAgentToUni(this.state.messages).map(message => message.messageId!),
              );
            }
            break;
          }
          case UserRole.Admin:
          case UserRole.Enroly: {
            if (this.state.unreadMessagesUniToAgentByAdmin > 0) {
              this.setState({
                unreadMessagesUniToAgentByAdmin: 0,
              });
              this.props.markMessagesRead(
                this.getUnreadMessagesFromUniToAgent(this.state.messages, 'admin').map(message => message.messageId!),
              );
            }
            break;
          }
          case UserRole.Agent:
          case UserRole.AgencyAdmin: {
            if (this.state.unreadMessagesUniToAgent > 0) {
              this.setState({
                unreadMessagesUniToAgent: 0,
              });
              this.props.markMessagesRead(
                this.getUnreadMessagesFromUniToAgent(this.state.messages, 'agent').map(message => message.messageId!),
              );
            }
            break;
          }
        }
        break;
      case 'agentStudent':
        switch (role) {
          case UserRole.Admin:
          case UserRole.Enroly: {
            if (this.state.unreadMessagesStudentToAgentByAdmin > 0) {
              this.setState({
                unreadMessagesStudentToAgentByAdmin: 0,
              });
              this.props.markMessagesRead(
                this.getUnreadMessagesFromStudentToAgent(this.state.messages, 'admin').map(
                  message => message.messageId!,
                ),
              );
            }
            break;
          }
          case UserRole.Agent:
          case UserRole.AgencyAdmin: {
            if (this.state.unreadMessagesStudentToAgent > 0) {
              this.setState({
                unreadMessagesStudentToAgent: 0,
              });
              this.props.markMessagesRead(
                this.getUnreadMessagesFromStudentToAgent(this.state.messages, 'agent').map(
                  message => message.messageId!,
                ),
              );
            }
            break;
          }
        }
        break;
    }
  };

  render() {
    const {
      unreadMessagesStudent,
      unreadMessagesUni,
      unreadMessagesStudentByUni,
      unreadMessagesUniToAgent,
      unreadMessagesStudentToAgent,
      unreadMessagesAgentToUni,
      unreadMessagesUniSecure,
      unreadMessagesUniByAgent,
      unreadMessagesUniByAdmin,
      unreadMessagesUniToStudentByAdmin,
      unreadMessagesUniToAgentByAdmin,
      unreadMessagesStudentToAgentByAdmin,
      unreadMessagesUniByPartner,
      unreadMessagesUniToStudentByPartner,
      unreadMessagesStudentByImmigrationProvider,
      unreadMessagesUniByImmigrationProvider,
      unreadMessagesUniToStudentByImmigrationProvider,
      role,
      visaAssistance,
      isAgent,
    } = this.state;
    const totalUnreadCount = getTotalUnreadCount(
      role,
      visaAssistance,
      isAgent,
      unreadMessagesStudent,
      unreadMessagesUni,
      unreadMessagesStudentByUni,
      unreadMessagesUniToAgent,
      unreadMessagesStudentToAgent,
      unreadMessagesAgentToUni,
      unreadMessagesUniSecure,
      unreadMessagesUniByAgent,
      unreadMessagesUniByAdmin,
      unreadMessagesUniToStudentByAdmin,
      unreadMessagesUniToAgentByAdmin,
      unreadMessagesStudentToAgentByAdmin,
      unreadMessagesUniByPartner,
      unreadMessagesUniToStudentByPartner,
      unreadMessagesStudentByImmigrationProvider +
        unreadMessagesUniByImmigrationProvider +
        unreadMessagesUniToStudentByImmigrationProvider,
    );

    const shouldShowAgentTabToUni =
      (visaAssistance && UNI_ROLES.includes(role)) || (isAgent && UNI_ROLES.includes(role));
    const shouldShowUniOrStudentTabToAgent =
      (visaAssistance && ENROLY_AGENT_ROLES.includes(role)) || (isAgent && AGENT_ROLES.includes(role));

    const MessageTabPanel = (props: { children?: React.ReactNode; index: number }) => {
      const { children, index } = props;

      return (
        <TabPanel
          key={index}
          id={`full-width-tabpanel-${index}`}
          p={0}
          overflow="auto"
          display="flex"
          flexDirection="column-reverse"
          height={{
            base: 'calc(100vh - 80px - 96px - 64px - 48px)',
            md: 'calc(100vh - 64px - 48px - 64px - 24px - 12px - 64px)',
          }}
          maxHeight="60rem"
          _focusVisible={{ boxShadow: 'outline' }}
        >
          <Box>{children}</Box>

          <Text textAlign={'center'} fontSize={'sm'} color={'muted'} mx={10} my={4}>
            {this.getTabDescriptionByType(this.state.tabIndexes.get(index))}
          </Text>
        </TabPanel>
      );
    };

    const handleChange = (newValue: number) => {
      this.setState(
        {
          tabIndex: newValue,
          message: '',
        },
        () => {
          this.updateUnreads(newValue, role);
        },
      );
    };

    const tabIndex = this.state.tabIndexes.get(this.state.tabIndex);

    const canSendPersonalNote =
      tabIndex === 'note' || tabIndex === 'agentStudent' || tabIndex === 'secure' || tabIndex === 'immigrationProvider';
    const messageSendButtonHidden =
      (tabIndex === 'note' && role === '[ImmigrationProvider]') || (tabIndex === 'note' && role === '[Partner]');
    // this mess is because swipeable views throws a huge amount of warnings if you
    // handle the showing of elements in the usual react way in the render
    const getTabPanels = () => {
      if (shouldShowAgentTabToUni) {
        if (role === '[Uni]' || role === '[UniAdmin]' || role === '[UniLite]') {
          return (
            <TabPanels>
              <MessageTabPanel index={0}>
                {this.state.messages && this.state.messages.length > 0
                  ? this.transformMessages(this.filterMessagesByType(this.state.messages, this.state.tabIndexes.get(0)))
                  : null}
              </MessageTabPanel>
              <MessageTabPanel index={1}>
                {this.state.messages && this.state.messages.length > 0
                  ? this.transformMessages(this.filterMessagesByType(this.state.messages, this.state.tabIndexes.get(1)))
                  : null}
              </MessageTabPanel>
            </TabPanels>
          );
        }

        if (role === '[UniSecure]') {
          return (
            <TabPanels>
              <MessageTabPanel index={0}>
                {this.state.messages && this.state.messages.length > 0
                  ? this.transformMessages(this.filterMessagesByType(this.state.messages, this.state.tabIndexes.get(0)))
                  : null}
              </MessageTabPanel>
              <MessageTabPanel index={1}>
                {this.state.messages && this.state.messages.length > 0
                  ? this.transformMessages(this.filterMessagesByType(this.state.messages, this.state.tabIndexes.get(1)))
                  : null}
              </MessageTabPanel>
              <MessageTabPanel index={2}>
                {this.state.messages && this.state.messages.length > 0
                  ? this.transformMessages(this.filterMessagesByType(this.state.messages, this.state.tabIndexes.get(2)))
                  : null}
              </MessageTabPanel>
            </TabPanels>
          );
        }
      }

      if (shouldShowUniOrStudentTabToAgent) {
        return (
          <TabPanels>
            <MessageTabPanel index={0}>
              {this.state.messages && this.state.messages.length > 0
                ? this.transformMessages(this.filterMessagesByType(this.state.messages, this.state.tabIndexes.get(0)))
                : null}
            </MessageTabPanel>
            <MessageTabPanel index={1}>
              {this.state.messages && this.state.messages.length > 0
                ? this.transformMessages(this.filterMessagesByType(this.state.messages, this.state.tabIndexes.get(1)))
                : null}
            </MessageTabPanel>
            <MessageTabPanel index={2}>
              {this.state.messages && this.state.messages.length > 0
                ? this.transformMessages(this.filterMessagesByType(this.state.messages, this.state.tabIndexes.get(2)))
                : null}
            </MessageTabPanel>
          </TabPanels>
        );
      }

      if (role === '[Uni]' || role === '[UniAdmin]' || role === '[UniLite]') {
        return (
          <TabPanels>
            <MessageTabPanel index={0}>
              {this.state.messages && this.state.messages.length > 0
                ? this.transformMessages(this.filterMessagesByType(this.state.messages, this.state.tabIndexes.get(0)))
                : null}
            </MessageTabPanel>
          </TabPanels>
        );
      }

      if (role === '[UniSecure]') {
        return (
          <TabPanels>
            <MessageTabPanel index={0}>
              {this.state.messages && this.state.messages.length > 0
                ? this.transformMessages(this.filterMessagesByType(this.state.messages, this.state.tabIndexes.get(0)))
                : null}
            </MessageTabPanel>
            <MessageTabPanel index={1}>
              {this.state.messages && this.state.messages.length > 0
                ? this.transformMessages(this.filterMessagesByType(this.state.messages, this.state.tabIndexes.get(1)))
                : null}
            </MessageTabPanel>
          </TabPanels>
        );
      }

      if (role === '[ImmigrationProvider]') {
        return (
          <TabPanels>
            <MessageTabPanel index={0}>
              {this.state.messages && this.state.messages.length > 0
                ? this.transformMessages(this.filterMessagesByType(this.state.messages, this.state.tabIndexes.get(0)))
                : null}
            </MessageTabPanel>
            <MessageTabPanel index={1}>
              {this.state.messages && this.state.messages.length > 0
                ? this.transformMessages(this.filterMessagesByType(this.state.messages, this.state.tabIndexes.get(1)))
                : null}
            </MessageTabPanel>
          </TabPanels>
        );
      }

      return (
        <TabPanels>
          <MessageTabPanel index={0}>
            {this.state.messages && this.state.messages.length > 0
              ? this.transformMessages(this.filterMessagesByType(this.state.messages, this.state.tabIndexes.get(0)))
              : null}
          </MessageTabPanel>
        </TabPanels>
      );
    };

    const isStudentTabVisible = [UserRole.UniSecure, UserRole.ImmigrationProvider].includes(role as UserRole);

    const getVisibilityDefaultValue = (): string => {
      if (this.state.hideMessages) {
        return 'notes';
      }
      if (this.state.hideNotes) {
        return 'messages';
      }
      return 'everything';
    };

    const tabStyle = {
      fontSize: { base: 'sm', md: 'md' },
      lineHeight: { base: 4, md: 5 },
      px: { base: 3, md: 5 },
    };

    return (
      <>
        <Box position="fixed" bottom={2.5} right={{ base: '5.5rem', md: 3, lg: 6 }} zIndex={{ base: 9, md: null }}>
          <Popover
            placement="top-end"
            gutter={12}
            arrowSize={12}
            flip={false}
            isLazy={true}
            lazyBehavior="keepMounted"
            strategy="fixed"
            onOpen={() => this.onClickMinimisedInbox(role, totalUnreadCount)}
          >
            <MessagesButton totalUnreadCount={totalUnreadCount} label={t('common:messagesNotes')} />

            <Portal>
              <PopoverContent width={{ base: '90vw', sm: 'md', md: 'lg', xl: 'xl' }}>
                <PopoverArrow />

                <Tabs
                  index={this.state.tabIndex}
                  onChange={index => {
                    handleChange(index);
                  }}
                >
                  <PopoverHeader p={0}>
                    <TabList height={12} borderColor="chakra-border-color">
                      <Tab position="relative" {...tabStyle}>
                        {[
                          '[Agent]',
                          '[AgencyAdmin]',
                          '[Enroly]',
                          '[Admin]',
                          '[ImmigrationProvider]',
                          '[Partner]',
                          '[UniSecure]',
                          '[Uni]',
                          '[UniAdmin]',
                          '[UniLite]',
                        ].includes(role)
                          ? 'Open channel'
                          : 'Student'}
                        <UnreadBadge
                          count={
                            UNI_ROLES.includes(role)
                              ? unreadMessagesUni + unreadMessagesStudentByUni
                              : ENROLY_AGENT_ROLES.includes(role)
                                ? unreadMessagesUniByAdmin + unreadMessagesUniToStudentByAdmin
                                : AGENT_ROLES.includes(role)
                                  ? unreadMessagesUniByAgent + unreadMessagesStudent
                                  : PARTNER_ROLES.includes(role)
                                    ? unreadMessagesUniByPartner + unreadMessagesUniToStudentByPartner
                                    : IMMIGRATION_PROVIDER_ROLES.includes(role)
                                      ? unreadMessagesUniByImmigrationProvider
                                      : 0
                          }
                        />
                      </Tab>

                      {shouldShowAgentTabToUni && (
                        <Tab position="relative" {...tabStyle}>
                          {visaAssistance ? 'Enroly' : 'Agent'}
                          <UnreadBadge count={unreadMessagesAgentToUni} />
                        </Tab>
                      )}

                      {isStudentTabVisible && (
                        <Tab position="relative" {...tabStyle}>
                          {t('common:studentOnly')}
                                                                          <UnreadBadge
                            count={
                              this.props.role === UserRole.UniSecure
                                ? unreadMessagesUniSecure
                                : this.props.role === UserRole.ImmigrationProvider
                                  ? unreadMessagesStudentByImmigrationProvider
                                  : 0
                            }
                          />
                        </Tab>
                      )}

                      {shouldShowUniOrStudentTabToAgent && (
                        <Tab position="relative" {...tabStyle}>
                          {t('common:universityOnly')}
                                                                          <UnreadBadge
                            count={
                              AGENT_ROLES.includes(role)
                                ? unreadMessagesUniToAgent
                                : ENROLY_AGENT_ROLES.includes(role)
                                  ? unreadMessagesUniToAgentByAdmin
                                  : 0
                            }
                          />
                        </Tab>
                      )}

                      {shouldShowUniOrStudentTabToAgent && (
                        <Tab position="relative" {...tabStyle}>
                          {t('common:studentOnly')}
                                                                          <UnreadBadge
                            count={
                              AGENT_ROLES.includes(role)
                                ? unreadMessagesStudentToAgent
                                : ENROLY_AGENT_ROLES.includes(role)
                                  ? unreadMessagesStudentToAgentByAdmin
                                  : 0
                            }
                          />
                        </Tab>
                      )}

                      <Menu placement="bottom-end" closeOnSelect={false}>
                        <Tooltip hasArrow placement="bottom" label={t('common:viewOptions')}>
                          <MenuButton
                            as={IconButton}
                            aria-label={t('common:actions')}
                            icon={<FontAwesomeIcon icon={faCog} />}
                            variant="ghost"
                            size="sm"
                            colorScheme="gray"
                            ml={'auto'}
                            mr={9}
                            mt={2}
                          />
                        </Tooltip>

                        <MenuList>
                          <MenuOptionGroup
                            title={t('common:show')}
                            type="radio"
                            defaultValue={getVisibilityDefaultValue()}
                            onChange={value => {
                              if (value === 'messages') {
                                this.setState({ hideNotes: true, hideMessages: false });
                              } else if (value === 'notes') {
                                this.setState({ hideNotes: false, hideMessages: true });
                              } else {
                                this.setState({ hideNotes: false, hideMessages: false });
                              }
                            }}
                          >
                            <MenuItemOption value="everything">{t('common:messagesNotes')}</MenuItemOption>
                            <MenuItemOption value="notes">{t('common:justNotes')}</MenuItemOption>
                            <MenuItemOption value="messages">{t('common:justMessages')}</MenuItemOption>
                          </MenuOptionGroup>
                        </MenuList>
                      </Menu>
                    </TabList>

                    <PopoverCloseButton top={2} right={1} width={8} height={8} />
                  </PopoverHeader>

                  <PopoverBody p={0}>{getTabPanels()}</PopoverBody>
                </Tabs>

                <PopoverFooter p={0} borderTop="2px solid" borderTopColor="chakra-border-color">
                  <Flex alignItems="end" gap={1} pr={2}>
                    <Textarea
                      id={'message'}
                      aria-label={t('common:writeYourNoteOrMessageHere')}
                      value={this.state.message}
                      onChange={event => this.setState({ message: event.currentTarget.value })}
                      placeholder={t('common:writeYourNoteOrMessageHere')}
                      rows={2}
                      minHeight={12}
                      maxHeight="10rem"
                      variant="unstyled"
                      px={4}
                      py={2}
                    />

                    {canSendPersonalNote ? (
                      <Tooltip hasArrow placement="top" label={this.state.message && 'Add as internal note'}>
                        <IconButton
                          aria-label={t('common:addAsInternalNote')}
                          id="submit-note-button"
                          onClick={() => {
                            this.setState({
                              isSendToStudent: false,
                            });
                            this.submitMessage(false);
                          }}
                          isDisabled={!this.state.message}
                          icon={<FontAwesomeIcon icon={faNoteMedical} />}
                          variant={'solid'}
                          colorScheme="yellow"
                          size="lg"
                          isRound
                          ml={1}
                          my={2}
                        />
                      </Tooltip>
                    ) : null}
                    {!messageSendButtonHidden ? (
                      <Tooltip hasArrow placement="top" label={this.state.message && 'Send message'}>
                        <IconButton
                          aria-label={t('common:sendMessage')}
                          id="submit-message-button"
                          onClick={() => {
                            this.setState(
                              {
                                isSendToStudent: true,
                              },
                              () => this.submitMessage(true),
                            );
                          }}
                          isDisabled={!this.state.message}
                          icon={<FontAwesomeIcon icon={faCommentMedical} />}
                          variant="solid"
                          size="lg"
                          isRound
                          ml={1}
                          my={2}
                        />
                      </Tooltip>
                    ) : null}
                  </Flex>
                </PopoverFooter>
              </PopoverContent>
            </Portal>
          </Popover>
        </Box>
      </>
    );
  }
}
